export const SOCKET_NAMESPACE_CORE = '/core';
export const SOCKET_NAMESPACE_CORE_SETUP = '/core/setup';
export const SOCKET_NAMESPACE_CLIENT = '/client';

export const enumID2Name =
  <T>(enumItem: object) =>
  (id: T) =>
    Object.entries(enumItem).find((t) => t[1] == id)?.[0] || null;

export enum DeploymentState {
  STOPPED = 1,
  CLONE_PENDING = 2,
  RUNNING = 5,
  DELETED_PENDING = 6,
  DELETED = 7,
  DELETED_ERROR = 8,
}

export const deploymentStateName = enumID2Name<DeploymentState>(DeploymentState);

export const DeploymentStateTexts = {
  [DeploymentState.STOPPED]: 'Stopped',
  [DeploymentState.CLONE_PENDING]: 'Cloning',
  [DeploymentState.RUNNING]: 'Running',
  [DeploymentState.DELETED_PENDING]: 'Deleting',
  [DeploymentState.DELETED]: 'Deleted',
  [DeploymentState.DELETED_ERROR]: 'Error deleting',
};

// Hm... everything but DELETED, is this smart?
export const deploymentNotDeletedStates = [
  DeploymentState.STOPPED,
  DeploymentState.CLONE_PENDING,
  DeploymentState.RUNNING,
  DeploymentState.DELETED_PENDING,
  DeploymentState.DELETED_ERROR, //TODO: Should this be included, or maybe there should be two groups?
];

export enum CoreState {
  INITIALIZING = 1, // Scheduled for creation, triggers taskInitializeCore()
  INITIALIZING_ERROR = 2, // Error during creation
  RUNNING_CONNECTING = 3, // Instance launch, waiting for core to come online
  RUNNING_ERROR = 4, // Something failed during launch, or just never connects
  RUNNING = 5,
  DELETED_PENDING = 6,
  DELETED = 7,
  DELETED_ERROR = 8,
}
export const coreStateName = enumID2Name<CoreState>(CoreState);

export const CoreStateTexts = {
  [CoreState.INITIALIZING]: 'Initializing',
  [CoreState.INITIALIZING_ERROR]: 'Error initializing',
  [CoreState.RUNNING_CONNECTING]: 'Connecting',
  [CoreState.RUNNING_ERROR]: 'Error',
  [CoreState.RUNNING]: 'Running',
  [CoreState.DELETED_PENDING]: 'Deleting',
  [CoreState.DELETED]: 'Deleted',
  [CoreState.DELETED_ERROR]: 'Error deleting',
};

export const coreStateToBooleans = (coreStateID: CoreState) => {
  const isError = coreStateID === CoreState.RUNNING_ERROR || coreStateID === CoreState.INITIALIZING_ERROR;
  const isDeleted = coreStateID === CoreState.DELETED;
  const isStarting = coreStateID === CoreState.RUNNING_CONNECTING;
  const isRunning =
    coreStateID === CoreState.RUNNING_CONNECTING || coreStateID === CoreState.RUNNING || coreStateID === CoreState.RUNNING_ERROR;
  const isDeleting = coreStateID === CoreState.DELETED_PENDING;
  const isInitializing = coreStateID === CoreState.INITIALIZING;
  const isLoading = isStarting || isDeleting || isInitializing;
  const canStop = [CoreState.RUNNING_CONNECTING, CoreState.RUNNING_ERROR, CoreState.RUNNING];
  const canTerminate = [CoreState.RUNNING_CONNECTING, CoreState.RUNNING_ERROR, CoreState.RUNNING, CoreState.INITIALIZING];
  const canStart = coreStateID == CoreState.DELETED;
  return {
    isError,
    isDeleted,
    isStarting,
    isRunning,
    isDeleting,
    isInitializing,
    isLoading,
    canStop,
    canTerminate,
    canStart,
  };
};

export enum RepositoryAccessType {
  /** Templates use this */
  PUBLIC = 1,
  /** The type used by normal repositories  */
  GITHUB_APP = 2,
  /** Used for repository less test mode, you can pull but should never be able to push */
  GITHUB_APP_DEMO_MODE_READ_ONLY = 3,
}

export enum JobState {
  RUNNING = 1,
  DONE_PENDING = 2, // Process should be shut down but not yet complete
  DONE = 3,
  FAILED = 4,
}
export const jobStateName = enumID2Name<JobState>(JobState);

export enum BuildType {
  BASE_IMAGE = 1,
  IMAGE = 2,
}

export const buildTypeName = enumID2Name<BuildType>(BuildType);

export enum BuildState {
  INIT = 1,
  RUNNING = 2,
  ERROR = 3,
  SUCCESS = 4,
}

export const buildStateName = enumID2Name<BuildState>(BuildState);

export enum DeploymentMetricType {
  CPU = 'cpu',
  MEMORY = 'memory',
  STATUS = 'status',
  SPECTRUM = 'spectrum',
}

export enum VolumeType {
  DEPLOYMENT = 1,
  SNAPSHOT = 2,
  CLONE = 3,
}

export const volumeTypeName = enumID2Name<VolumeType>(VolumeType);

export enum VolumeCoreState {
  INITIALIZING = 1,
  IN_USE = 2,
  IDLE = 3,
  PENDING_CLONE = 4,
  PENDING_DELETE = 6,
  DELETED = 7,
}

export const volumeCoreStateName = enumID2Name<VolumeCoreState>(VolumeCoreState);

export enum VolumeApiState {
  INITIALIZING = 1,
  IN_USE = 2,
  IDLE = 3,
  PENDING_CLONE = 4,
  PENDING_DELETE = 6,
  DELETED = 7,
}

export const volumeApiStateName = enumID2Name<VolumeApiState>(VolumeApiState);

// Convert volume api state to core state
// NOTE! They are identical as of now, and should probably be kept identical
export const volumeApiState2CoreState = (apiStateID: VolumeApiState) => {
  return apiStateID as unknown as VolumeCoreState;
};

export enum DeletingState {
  NOT_DELETED = 1,
  PENDING = 2,
  DONE = 3,
}

export const deletingStateName = enumID2Name<DeletingState>(DeletingState);

// Hardcoded access role ids
export enum AccessRole {
  USER = 1,
  ADMINISTRATOR = 2,
  DEVELOPER = 3,
  TRIAL_USER = 4,
  ANONYMOUS_USER = 5,
}

export enum DiploiErrors {
  DEPLOYMENT_DISABLED = 460,
  DEPLOYMENT_NOT_FOUND = 461,
  DEPLOYMENT_STARTING = 462,
}

export const DiploiErrorTexts = {
  [DiploiErrors.DEPLOYMENT_DISABLED]: 'Deployment is disabled',
  [DiploiErrors.DEPLOYMENT_NOT_FOUND]: 'Deployment not found',
  [DiploiErrors.DEPLOYMENT_STARTING]: 'Connecting to deployment…',
};

export const DiploiErrorDescriptions = {
  [DiploiErrors.DEPLOYMENT_DISABLED]: 'Please enable it from the Diploi dashboard.',
  [DiploiErrors.DEPLOYMENT_NOT_FOUND]: 'Please check the Diploi dashboard for more information.',
  [DiploiErrors.DEPLOYMENT_STARTING]:
    'Deployment could be starting up, or not responding. Please check the Diploi dashboard for more information.',
};

export enum AccountUserRole {
  ADMINISTRATOR = 1,
  MEMBER = 2,
}

export enum AccountPlan {
  DEVELOPER = 1,
  FREE = 2,
  PAID = 3,
  ANONYMOUS = 4,
}

export const ACCOUNT_FREE_PLAN_RUN_HOURS = 8;
export const ACCOUNT_ANONYMOUS_PLAN_SOFT_LIMIT_HOURS = 1;
export const ACCOUNT_ANONYMOUS_PLAN_HARD_LIMIT_HOURS = 24;

export const AccountPlanTexts: Record<AccountPlan, string> = {
  [AccountPlan.DEVELOPER]: 'Developer',
  [AccountPlan.FREE]: 'Free',
  [AccountPlan.PAID]: 'Paid',
  [AccountPlan.ANONYMOUS]: 'Anonymous',
};

export const AccountPlanInfos: Record<AccountPlan, string> = {
  [AccountPlan.DEVELOPER]: 'Access to all features & DLC', // DLC?
  [AccountPlan.FREE]: 'Access to all features with certain limits',
  [AccountPlan.PAID]: 'Access to all features',
  [AccountPlan.ANONYMOUS]: 'Time limited trial account',
};

export enum TokenType {
  ACCOUNT_INVITE = 1,
  PILOT_INVITE = 2,
  LOAD_TEMPLATE = 3,
  CODE_EDITOR_LOGIN = 4,
  ANONYMOUS_TRIAL = 5,
}

/** Expiration times for each token type */
export const TokenTypeTTLInMilliseconds: Record<TokenType, number> = {
  [TokenType.ACCOUNT_INVITE]: 1000 * 60 * 60 * 24 * 7, // 1 week
  [TokenType.PILOT_INVITE]: 1000 * 60 * 60 * 24 * 7, // 1 week
  [TokenType.LOAD_TEMPLATE]: 1000 * 60 * 60 * 1, // 1 h
  [TokenType.CODE_EDITOR_LOGIN]: 1000 * 60 * 5, // 5 min
  [TokenType.ANONYMOUS_TRIAL]: 1000 * 60 * 5, // 5 min
};

export enum DomainType {
  /** Project default domain, default deployment domains is (by default) created by prepeding xxxx. to this  */
  PROJECT = 1,
  /** Domain pointing at a core (or deployment) for direct access to port 22, NOTE! Also used as base for editor domains */
  CORE = 2,
  /** User-created base project domain name  */
  TWEAKED_PROJECT = 3,
  /** Default deployment domain name  */
  DEPLOYMENT = 4,
  /** User-created deployment domain name  */
  TWEAKED_DEPLOYMENT = 5,
}

export const DIPLOI_VPC_ID = 1;

export enum StackType {
  RUNNING = 1,
  CODE = 2,
  REPOSITORY = 3,
}
export const stackTypeName = enumID2Name<StackType>(StackType);

export enum ImageType {
  DEFAULT = 1,
  PREBUILD = 2,
}

export const UserPreferenceType = {
  PIN: 'pin',
  EDITORS: 'editors',
} as const;
